import type {
  NotificationVariable,
  WorkflowDefinitionPhaseResponse} from 'src/api/ProactApiClient';
import {
  NotificationVariableTypeEnum,
  TaskTypeEnum,
  WorkflowDefinitionActionTypeEnum
} from 'src/api/ProactApiClient';

interface ActionOutputVariableDefinition {
  name: string;
  description: string;
  example: string;
}

const approvalOutputVariables: ActionOutputVariableDefinition[] = [
  {name: 'STATUS', description: 'Approval outcome', example: 'Rejected'},
  {
    name: 'REASON',
    description: 'Approver comment(s)',
    example: 'Needs revision',
  },
  {
    name: 'APPROVER_NAME',
    description: 'Name(s) of approver(s)',
    example: 'Jane Smith',
  },
];

const actionOutputVariables: Record<string, ActionOutputVariableDefinition[]> = {
  APPROVAL: approvalOutputVariables,
};

function getActionRefLabel(
  actionType: WorkflowDefinitionActionTypeEnum,
  taskType?: TaskTypeEnum
): string | null {
  if (actionType !== WorkflowDefinitionActionTypeEnum.CreateTask) return null;

  switch (taskType) {
    case TaskTypeEnum.Approval:
      return 'APPROVAL';
    case TaskTypeEnum.Actions:
      return 'TASK';
    case TaskTypeEnum.ToolboxTalk:
      return 'TOOLBOXTALK';
    case TaskTypeEnum.Form:
      return 'FORM_TASK';
    default:
      return null;
  }
}

export interface PhaseVariableGroup {
  phaseSlug: string;
  phaseName: string;
  variables: NotificationVariable[];
}

/**
 * Compute available phase-scoped variables from the workflow definition state.
 * Only considers phases before `currentPhaseIndex` that have slugs and actions with output variables.
 */
export function computePhaseVariables(
  phases: WorkflowDefinitionPhaseResponse[],
  currentPhaseIndex: number
): PhaseVariableGroup[] {
  const groups: PhaseVariableGroup[] = [];

  for (let i = 0; i < currentPhaseIndex; i++) {
    const phase = phases[i];
    if (!phase?.slug) continue;

    const phaseVars: NotificationVariable[] = [];
    const labelCounts: Record<string, number> = {};

    for (const action of phase.actions ?? []) {
      if (!action.type) continue;
      const label = getActionRefLabel(
        action.type,
        action.metadata?.createTaskMetadata?.taskType
      );
      if (!label) continue;

      const count = (labelCounts[label] ?? 0) + 1;
      labelCounts[label] = count;
      const actionRef = `${label}_${count}`;

      const definitions = actionOutputVariables[label];
      if (!definitions) continue;

      for (const def of definitions) {
        const token = `${phase.slug}.${actionRef}.${def.name}`;
        phaseVars.push({
          type: NotificationVariableTypeEnum.Module,
          variable: token,
          description: `${def.description} from ${phase.name ?? phase.slug}`,
          example: def.example,
        });
      }
    }

    if (phaseVars.length > 0) {
      groups.push({
        phaseSlug: phase.slug,
        phaseName: phase.name ?? phase.slug,
        variables: phaseVars,
      });
    }
  }

  return groups;
}
